// /book-consultation
//
// Replaces the old "Book consultation" → /therapy-psychiatry dead-end
// with a deliberate fork:
//   1) "I'm new to AUMHUM"   → /care/get-started?intent=consultation
//   2) "I already have an account" → opens the auth modal (login)
//
// /therapy-psychiatry was a clinician directory. Sending visitors there
// from "Book consultation" CTAs felt circular (the directory's hero
// linked back to /how-care-works) and left them no clear way to
// actually book. This page does one job: ask the question that decides
// the next step, then send them down the right path.
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { APP_CONSTANTS } from 'src/consumer/constants'

import {
  Container,
  Eyebrow,
  Lede,
  SectionTitle,
} from './sections'
import {
  ArrowRight,
  Brain,
  CheckCircle2,
  ClipboardList,
  MessageCircle,
  PhoneCall,
  Repeat,
  ShieldCheck,
  Stethoscope,
  UserCheck,
} from './icons'

const WHATSAPP_PREFILL = 'Hi, I would like to book a consultation.'

function buildPrefilledWhatsappUrl(baseUrl, message) {
  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}text=${encodeURIComponent(message)}`
}

// `onLogin` opens the existing ClubbedAuthModal owned by AumhumSite.
// Wiring it as a prop keeps this view dumb — we don't manage auth
// state here, we just trigger the modal.
export default function BookConsultationView({ onLogin }) {
  const whatsappUrl = useMemo(
    () => buildPrefilledWhatsappUrl(APP_CONSTANTS.PATIENT_CARE_WHATSAPP_LINK, WHATSAPP_PREFILL),
    [],
  )

  return (
    <main id="main">
      {/* ── Hero ─ ask the fork question first, big and clear ───── */}
      <section className="as-care-hero as-care-hero--centered">
        <Container>
          <div className="as-care-hero-stack">
            <Eyebrow>Book a consultation</Eyebrow>
            <h1 className="as-h1 as-care-hero-title">
              Talk to the right{' '}
              <span className="as-brand-gradient">specialist</span>{' '}
              for your concern.
            </h1>
            <Lede className="as-care-hero-lede">
              Tell us a little about what you need and we&rsquo;ll match you to a
              vetted clinician — therapist, psychiatrist, endocrinologist, or
              another specialist — and book a time that works.
            </Lede>

            {/* Two-path fork. The card sits inside the hero so it's the
                first thing the user sees; everything below this is
                supporting context. */}
            <div className="bc-paths" role="group" aria-label="Choose how to begin">
              <div className="bc-path-card bc-path-card--new">
                <div className="bc-path-icon">
                  <UserCheck />
                </div>
                <div className="bc-path-body">
                  <p className="as-care-kicker">First time here</p>
                  <h2 className="as-h3">I&rsquo;m new to AUMHUM</h2>
                  <p className="as-p">
                    Quick mobile sign-up, then a few questions so we can match you to
                    the right specialist.
                  </p>
                  <Link
                    to="/care/get-started?intent=consultation"
                    className="as-btn as-btn-primary bc-path-cta"
                  >
                    Get matched <ArrowRight />
                  </Link>
                </div>
              </div>

              <div className="bc-path-card bc-path-card--returning">
                <div className="bc-path-icon bc-path-icon--alt">
                  <ShieldCheck />
                </div>
                <div className="bc-path-body">
                  <p className="as-care-kicker">Returning</p>
                  <h2 className="as-h3">I already have an account</h2>
                  <p className="as-p">
                    Sign in to continue your care, see your specialist, and manage
                    upcoming sessions.
                  </p>
                  <button
                    type="button"
                    className="as-btn as-btn-secondary bc-path-cta"
                    onClick={() => {
                      if (typeof onLogin === 'function') onLogin()
                    }}
                  >
                    Log in <ArrowRight />
                  </button>
                </div>
              </div>
            </div>

            <ul className="as-care-hero-trust" aria-label="What this means">
              <li className="as-trust-item"><ShieldCheck /> Confidential</li>
              <li className="as-trust-item"><UserCheck /> Reviewed by our care team</li>
              <li className="as-trust-item"><Repeat /> No commitment to book</li>
            </ul>

            <div className="as-care-note as-care-note--centered">
              If you or someone you know is in immediate danger, please contact
              local emergency services right away.
            </div>
          </div>
        </Container>
      </section>

      {/* ── How a consultation works ─ 3 quiet steps ─────────── */}
      <section className="as-section as-section-white">
        <Container>
          <div className="as-care-howit-head">
            <Eyebrow>How a consultation works</Eyebrow>
            <SectionTitle>From hello to booked, in three steps.</SectionTitle>
          </div>
          <ol className="as-care-howit-grid">
            <li className="as-care-howit-step">
              <span className="as-care-howit-num">1</span>
              <div className="as-care-howit-icon"><ClipboardList /></div>
              <h3 className="as-h3">Tell us what you need</h3>
              <p className="as-p">
                Pick the area of care — therapy, psychiatry, hormonal, immune,
                child, or not sure — and share a few details.
              </p>
            </li>
            <li className="as-care-howit-step">
              <span className="as-care-howit-num">2</span>
              <div className="as-care-howit-icon"><Brain /></div>
              <h3 className="as-h3">We match you to a specialist</h3>
              <p className="as-p">
                Our care team reviews what you shared and lines up a vetted
                clinician whose practice fits your situation.
              </p>
            </li>
            <li className="as-care-howit-step">
              <span className="as-care-howit-num">3</span>
              <div className="as-care-howit-icon"><Stethoscope /></div>
              <h3 className="as-h3">Book your consultation</h3>
              <p className="as-p">
                Pick a time, online or in-person where available, and meet your
                specialist. Follow-ups stay with the same care team.
              </p>
            </li>
          </ol>
          <ul className="bc-howit-trust">
            <li><CheckCircle2 /> Same-day care-team response in most cases</li>
            <li><CheckCircle2 /> Continuity across therapists, doctors, and follow-ups</li>
            <li><CheckCircle2 /> Your details stay with the assigned care team only</li>
          </ul>
        </Container>
      </section>

      {/* ── Other ways to reach us ─────────────────────────── */}
      <section className="as-section as-section-alt">
        <Container>
          <div className="as-care-altways-head">
            <Eyebrow>Prefer to talk first?</Eyebrow>
            <SectionTitle>Other ways to reach our care team</SectionTitle>
          </div>
          <div className="as-care-altways">
            <a
              href={whatsappUrl}
              className="as-care-altway"
              target="_blank"
              rel="noreferrer"
            >
              <span className="as-care-altway-icon"><MessageCircle /></span>
              <span className="as-care-altway-body">
                <span className="as-care-kicker">Talk to us</span>
                <span className="as-h3">Message on WhatsApp</span>
                <span className="as-p">
                  Reply when it suits you — our care team will pick up.
                </span>
                <span className="as-link as-link-violet as-care-entry-cta">
                  Open WhatsApp <ArrowRight />
                </span>
              </span>
            </a>
            <a
              href={APP_CONSTANTS.SUPPORT_SCHEDULE_CALL}
              className="as-care-altway"
              target="_blank"
              rel="noreferrer"
            >
              <span className="as-care-altway-icon"><PhoneCall /></span>
              <span className="as-care-altway-body">
                <span className="as-care-kicker">Want a call?</span>
                <span className="as-h3">Get a call back</span>
                <span className="as-p">
                  Share your number and a good time — a coordinator will call you.
                </span>
                <span className="as-link as-link-violet as-care-entry-cta">
                  Pick a time <ArrowRight />
                </span>
              </span>
            </a>
          </div>
        </Container>
      </section>
    </main>
  )
}
